import { createSignal, createEffect, For, Show, onCleanup } from 'solid-js';
import { A, useParams, useNavigate } from '@solidjs/router';
import { useSFetch } from '../hooks/sfetch';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { showConfirm } from '../components/Dialog';
import StateDisplay from '../components/StateDisplay';
import Text from '../components/Text';
import { Tenant, TenantRole } from '../models/Tenant';
import styles from './Tenants.module.css';
import membersStyles from './TenantMembers.module.css';
import { firstError } from '../api/envelope';

interface TenantMemberInfo {
    id: string;
    userId: string;
    role: TenantRole;
    isActive: boolean;
    user?: { id: string; username: string };
    username?: string;
}

export default function AdminTenantDetails() {
    const sfetch = useSFetch();
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const params = useParams<{ id: string }>();

    const [loading, setLoading] = createSignal(true);
    const [tenant, setTenant] = createSignal<Tenant | null>(null);
    const [editName, setEditName] = createSignal('');
    const [editActive, setEditActive] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    const [members, setMembers] = createSignal<TenantMemberInfo[]>([]);
    const [loadingMembers, setLoadingMembers] = createSignal(true);

    const [search, setSearch] = createSignal('');
    const [results, setResults] = createSignal<{ id: string; username: string }[]>([]);
    const [pickedUserId, setPickedUserId] = createSignal('');
    const [newRole, setNewRole] = createSignal<TenantRole>(TenantRole.Member);
    const [adding, setAdding] = createSignal(false);

    async function loadTenant() {
        setLoading(true);
        try {
            const res = await api.tenantAdmin.getTenant(params.id);
            if (isOk(res.data)) {
                const found = res.data.value as Tenant;
                setTenant(found);
                setEditName(found.name);
                setEditActive(found.isActive);
            } else {
                setTenant(null);
            }
        } finally {
            setLoading(false);
        }
    }

    async function loadMembers() {
        setLoadingMembers(true);
        try {
            const res = await api.tenantAdmin.getMembers(params.id);
            if (isOk(res.data)) setMembers((res.data.value.members ?? []) as TenantMemberInfo[]);
        } finally {
            setLoadingMembers(false);
        }
    }

    createEffect(() => {
        if (params.id) {
            void loadTenant();
            void loadMembers();
        }
    });

    let debounceHandle: number | undefined;
    createEffect(() => {
        const term = search().trim();
        if (debounceHandle) window.clearTimeout(debounceHandle);
        if (!term || pickedUserId()) { setResults([]); return; }
        debounceHandle = window.setTimeout(async () => {
            const res = await sfetch('/api/User/Users');
            if (res.ok && res.value) {
                const existing = new Set(members().map(m => m.userId));
                const matches = (res.value as { id: string; username: string }[])
                    .filter(u => u.username.toLowerCase().includes(term.toLowerCase()))
                    .filter(u => !existing.has(u.id))
                    .slice(0, 8);
                setResults(matches);
            }
        }, 200);
    });
    onCleanup(() => { if (debounceHandle) window.clearTimeout(debounceHandle); });

    function pickUser(u: { id: string; username: string }) {
        setPickedUserId(u.id);
        setSearch(u.username);
        setResults([]);
    }

    async function saveTenant() {
        setSaving(true);
        try {
            const res = await api.tenantAdmin.updateTenant(params.id, { name: editName(), isActive: editActive() });
            if (isOk(res.data)) {
                toast?.info(t('TENANT_SETTINGS_SAVED'));
                await loadTenant();
            } else {
                toast?.error(firstError(res.data) ?? t('TENANT_SAVE_FAILED'));
            }
        } finally {
            setSaving(false);
        }
    }

    async function deleteTenant() {
        if (!await showConfirm(t('ADMIN_TENANT_DELETE_CONFIRM'), true)) return;
        const res = await api.tenantAdmin.deleteTenant(params.id);
        if (isOk(res.data)) {
            toast?.info(t('ADMIN_TENANT_DELETED'));
            navigate('/admin/tenants');
        }
    }

    async function addMember() {
        if (!pickedUserId()) return;
        setAdding(true);
        try {
            const res = await api.tenantAdmin.addMember(params.id, { userId: pickedUserId(), role: newRole() });
            if (isOk(res.data)) {
                toast?.info(t('TENANT_MEMBER_ADDED'));
                setSearch('');
                setPickedUserId('');
                setResults([]);
                setNewRole(TenantRole.Member);
                await loadMembers();
            } else {
                toast?.error(firstError(res.data) ?? t('TENANT_MEMBER_ADD_FAILED'));
            }
        } finally {
            setAdding(false);
        }
    }

    async function removeMember(userId: string) {
        if (!await showConfirm(t('ADMIN_TENANT_DELETE_CONFIRM'), true)) return;
        const res = await api.tenantAdmin.removeMember(params.id, userId);
        if (isOk(res.data)) {
            toast?.info(t('TENANT_MEMBER_REMOVED'));
            await loadMembers();
        }
    }

    async function changeRole(userId: string, role: TenantRole) {
        const res = await api.tenantAdmin.updateMemberRole(params.id, userId, { role });
        if (isOk(res.data)) {
            toast?.info(t('TENANT_ROLE_UPDATED'));
            await loadMembers();
        }
    }

    function initial(name?: string) {
        return (name ?? '?').trim().slice(0, 1).toUpperCase();
    }

    function roleBadgeClass(role: TenantRole) {
        switch (role) {
            case TenantRole.Owner: return membersStyles.roleBadgeOwner;
            case TenantRole.Admin: return membersStyles.roleBadgeAdmin;
            default: return membersStyles.roleBadgeMember;
        }
    }

    return (
        <div class={styles.container}>
            <A href="/admin/tenants" class={styles.backLink}>
                <i class="fa-solid fa-arrow-left"></i>
                <Text val="ADMIN_TENANT_BACK_TO_LIST" />
            </A>

            <Show when={loading()}>
                <StateDisplay loading />
            </Show>

            <Show when={!loading() && !tenant()}>
                <StateDisplay empty icon="fa-solid fa-building-circle-xmark" message="ADMIN_TENANT_NOT_FOUND" />
            </Show>

            <Show when={!loading() && tenant()}>
                <div class={styles.header}>
                    <div class={styles.titleSection}>
                        <div class={styles.cardAvatar}>
                            <i class="fa-solid fa-building"></i>
                        </div>
                        <div class={styles.titleText}>
                            <h1>{tenant()?.name}</h1>
                            <p class={styles.subtitle}>@{tenant()?.slug} &middot; {tenant()?.id}</p>
                        </div>
                    </div>
                    <div class={styles.headerActions}>
                        <A href={`/admin/tenant/${params.id}/grants`} class="btn btn-secondary">
                            <i class="fa-solid fa-coins" />
                            <Text val="ADMIN_TENANT_MANAGE_LICENSES" />
                        </A>
                        <button class="btn btn-danger" onClick={deleteTenant}>
                            <i class="fa-solid fa-trash" />
                            <Text val="DELETE" />
                        </button>
                    </div>
                </div>

                <div class={styles.section}>
                    <div class={styles.sectionHeader}>
                        <h2><Text val="ADMIN_TENANT_EDIT" /></h2>
                    </div>
                    <div class={styles.formCard}>
                        <label class={styles.formField}>
                            <span class={styles.fieldLabel}><Text val="TENANT_NAME" /></span>
                            <input
                                type="text"
                                class={styles.input}
                                value={editName()}
                                onInput={e => setEditName(e.currentTarget.value)}
                            />
                        </label>
                        <label class="hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <input
                                type="checkbox"
                                checked={editActive()}
                                onChange={e => setEditActive(e.currentTarget.checked)}
                            />
                            <span class={styles.fieldLabel}><Text val="ACTIVE" /></span>
                        </label>
                        <div class={styles.formActions}>
                            <button class="btn btn-primary" onClick={saveTenant} disabled={saving()}>
                                <Show when={saving()} fallback={<i class="fa-solid fa-floppy-disk" />}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                                <Text val={saving() ? 'TENANT_SAVING' : 'SAVECHANGES'} />
                            </button>
                        </div>
                    </div>
                </div>

                <div class={styles.section}>
                    <div class={styles.sectionHeader}>
                        <h2><Text val="TENANT_ADD_MEMBER" /></h2>
                    </div>
                    <div class={styles.formCard}>
                        <div class={styles.formField}>
                            <span class={styles.fieldLabel}><Text val="TENANT_USERNAME" /></span>
                            <input
                                type="text"
                                class={styles.input}
                                placeholder={t('TENANT_SEARCH_USERNAME')}
                                value={search()}
                                onInput={e => {
                                    setSearch(e.currentTarget.value);
                                    setPickedUserId('');
                                }}
                            />
                            <Show when={results().length > 0}>
                                <div class={styles.searchResults}>
                                    <For each={results()}>
                                        {u => (
                                            <div
                                                class={`${styles.searchResult}${pickedUserId() === u.id ? ` ${styles.selected}` : ''}`}
                                                onClick={() => pickUser(u)}
                                            >
                                                <i class="fa-solid fa-user"></i>
                                                <span>{u.username}</span>
                                            </div>
                                        )}
                                    </For>
                                </div>
                            </Show>
                        </div>
                        <label class={styles.formField}>
                            <span class={styles.fieldLabel}><Text val="TENANT_ROLE" /></span>
                            <select
                                class={styles.select}
                                value={newRole()}
                                onChange={e => setNewRole(parseInt(e.currentTarget.value) as TenantRole)}
                            >
                                <option value={TenantRole.Member}>{t('TENANT_ROLE_MEMBER')}</option>
                                <option value={TenantRole.Admin}>{t('TENANT_ROLE_ADMIN')}</option>
                                <option value={TenantRole.Owner}>{t('TENANT_ROLE_OWNER')}</option>
                            </select>
                        </label>
                        <div class={styles.formActions}>
                            <button class="btn btn-primary" onClick={addMember} disabled={adding() || !pickedUserId()}>
                                <Show when={adding()} fallback={<i class="fa-solid fa-plus" />}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                                <Text val="TENANT_ADD" />
                            </button>
                        </div>
                    </div>
                </div>

                <div class={styles.section}>
                    <div class={styles.sectionHeader}>
                        <h2><Text val="TENANT_MEMBERS_LIST" /></h2>
                        <span class={membersStyles.sectionBadge}>
                            <i class="fa-solid fa-hashtag"></i>
                            {members().length}
                        </span>
                    </div>

                    <Show when={loadingMembers()}>
                        <StateDisplay loading />
                    </Show>
                    <Show when={!loadingMembers() && members().length === 0}>
                        <StateDisplay empty icon="fa-solid fa-users" message="TENANT_NO_MEMBERS" />
                    </Show>
                    <Show when={!loadingMembers() && members().length > 0}>
                        <div class={membersStyles.grid}>
                            <For each={members()}>
                                {(m) => {
                                    const username = m.username ?? m.user?.username ?? m.userId;
                                    return (
                                        <div class={membersStyles.memberCard}>
                                            <div class={membersStyles.memberHead}>
                                                <div class={membersStyles.memberAvatar}>{initial(username)}</div>
                                                <div class={membersStyles.memberInfo}>
                                                    <div class={membersStyles.memberName}>{username}</div>
                                                    <span class={`${membersStyles.roleBadge} ${roleBadgeClass(m.role)}`}>
                                                        <Show when={m.role === TenantRole.Owner}><i class="fa-solid fa-crown"></i></Show>
                                                        <Show when={m.role === TenantRole.Admin}><i class="fa-solid fa-user-shield"></i></Show>
                                                        <Show when={m.role === TenantRole.Member}><i class="fa-solid fa-user"></i></Show>
                                                        {TenantRole[m.role]}
                                                    </span>
                                                </div>
                                            </div>
                                            <div class={membersStyles.memberActions}>
                                                <select
                                                    class={membersStyles.memberRoleSelect}
                                                    value={m.role}
                                                    onChange={e => changeRole(m.userId, parseInt(e.currentTarget.value) as TenantRole)}
                                                >
                                                    <option value={TenantRole.Member}>{t('TENANT_ROLE_MEMBER')}</option>
                                                    <option value={TenantRole.Admin}>{t('TENANT_ROLE_ADMIN')}</option>
                                                    <option value={TenantRole.Owner}>{t('TENANT_ROLE_OWNER')}</option>
                                                </select>
                                                <button class={membersStyles.removeBtn} onClick={() => removeMember(m.userId)} title={t('TENANT_REMOVE')}>
                                                    <i class="fa-solid fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }}
                            </For>
                        </div>
                    </Show>
                </div>
            </Show>
        </div>
    );
}
